import React, { useState, useEffect } from 'react';
import { Badge, Button, Card, Note, Sym } from '../../../components/app2';
import authService from '../../../utils/authService';
import AlertService from '../../../utils/alertService';

/**
 * Estado de seguridad de la cuenta (rediseño jul-2026).
 *
 * SE RETIRÓ LO QUE ERA SIMULADO. Esta tarjeta mostraba tres cosas inventadas:
 *   · "Sesiones activas": una sesión fija escrita a mano ("Chrome en Windows ·
 *     México, CDMX"), con un botón de cerrar sesión que solo la borraba de la
 *     pantalla —no cerraba nada— porque no existe registro de sesiones.
 *   · "Actividad reciente": dos entradas fijas ("Inicio de sesión exitoso",
 *     "Cambio de configuración") generadas en el propio componente.
 *   · Un "puntaje de seguridad" que partía de 70 y restaba 10 por intento
 *     fallido: un número sin respaldo.
 *
 * Queda lo que SÍ es real y verificable:
 *   · Estado de la cuenta y si el sistema exige cambiar la contraseña.
 *   · Cuándo se cambió la contraseña por última vez.
 *   · Los intentos fallidos de inicio de sesión de ESTE navegador (el candado
 *     antifuerza-bruta vive en el navegador) y el botón que los reinicia, que
 *     era y sigue siendo funcional.
 */

const fechaHora = (valor) => (valor ? new Date(valor)?.toLocaleString('es-MX', {
  dateStyle: 'medium', timeStyle: 'short',
}) : null);

const Renglon = ({ icon, iconColor, label, children, ultimo = false }) => (
  <div
    style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 12, padding: '11px 0',
      borderBottom: ultimo ? 'none' : '1px solid var(--line)',
    }}
  >
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
      <Sym name={icon} size={18} style={{ color: iconColor, flexShrink: 0 }} />
      <span className="a2-tcell">{label}</span>
    </span>
    <span style={{ textAlign: 'right', flexShrink: 0 }}>{children}</span>
  </div>
);

const SecuritySection = ({ userData, profileData }) => {
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);

  const cargarEstado = () => {
    const loginStatus = authService?.getLoginAttemptStatus();
    setLoginAttempts(loginStatus?.attempts || 0);
    setIsBlocked(Boolean(loginStatus?.isBlocked));
  };

  useEffect(() => {
    cargarEstado();
  }, []);

  const perfil = profileData?.user || userData || {};
  const activa = perfil?.is_active ?? userData?.is_active;
  const debeCambiar = perfil?.must_change_password ?? userData?.must_change_password;
  const ultimoCambio = fechaHora(perfil?.password_changed_at ?? userData?.password_changed_at);

  const handleResetAttempts = async () => {
    const confirmado = await AlertService?.confirm(
      '¿Reiniciar los intentos fallidos?',
      'Se pone en cero el contador de este navegador y se levanta el bloqueo temporal de inicio de sesión.',
      { confirmText: 'Reiniciar', cancelText: 'Cancelar' },
    );
    if (!confirmado) return;

    const resultado = authService?.resetLoginAttempts();
    cargarEstado();

    if (resultado?.success) {
      await AlertService?.success('Listo', resultado?.message, { autoClose: true });
    }
  };

  return (
    <Card>
      <h3 className="a2-card-title" style={{ fontSize: '1.1rem' }}>Seguridad</h3>
      <p className="a2-card-sub" style={{ marginBottom: 12 }}>
        Estado de acceso de tu cuenta
      </p>

      <Renglon
        icon={activa ? 'check_circle' : 'cancel'}
        iconColor={activa ? 'var(--ok)' : 'var(--danger)'}
        label="Cuenta"
      >
        <Badge tone={activa ? 'ok' : 'danger'}>{activa ? 'Activa' : 'Inactiva'}</Badge>
      </Renglon>

      <Renglon
        icon={debeCambiar ? 'key_off' : 'key'}
        iconColor={debeCambiar ? 'var(--warn)' : 'var(--brand-2)'}
        label="Contraseña"
      >
        {debeCambiar ? (
          <Badge tone="warn" icon="warning">Debe cambiarse</Badge>
        ) : (
          <span className="a2-tcell-strong">
            {ultimoCambio ? `Actualizada el ${ultimoCambio}` : 'Configurada'}
          </span>
        )}
      </Renglon>

      <Renglon
        icon={loginAttempts > 0 ? 'warning' : 'verified_user'}
        iconColor={loginAttempts > 0 ? 'var(--warn)' : 'var(--ok)'}
        label="Intentos fallidos en este navegador"
        ultimo
      >
        <span
          className="a2-tcell-strong"
          style={{ color: loginAttempts > 0 ? 'var(--warn)' : 'var(--ok)' }}
        >
          {loginAttempts}
        </span>
      </Renglon>

      {isBlocked ? (
        <Note tone="danger" style={{ marginTop: 16 }}>
          El inicio de sesión está bloqueado temporalmente en este navegador por intentos fallidos.
        </Note>
      ) : null}

      {loginAttempts > 0 ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <Button variant="outline" size="sm" icon="restart_alt" onClick={handleResetAttempts}>
            Reiniciar intentos fallidos
          </Button>
        </div>
      ) : null}

      <Note tone="info" style={{ marginTop: 16 }}>
        Si notas algo raro en tu cuenta, cambia tu contraseña de inmediato desde esta misma pestaña.
      </Note>
    </Card>
  );
};

export default SecuritySection;
